import {
  AutocompleteWithLabel,
  FreeSoloAutocompleteWithLabel,
  InputWithLabel,
  SelectWithLabel
} from '@adotel/components'
import {
  LazyQueryExecFunction,
  OperationVariables,
  useLazyQuery,
  useQuery
} from '@apollo/client'
import { Button, CircularProgress, MenuItem } from '@material-ui/core'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'

import TimePeriodForm from '../../components/TimePeriodForm'
import { ENTITY_LIST_QUERY } from '../../graphql/entityList'
import { AUDIT_LOG_ROLE_NAMES_QUERY } from '../../graphql/role'
import { USERS_LIST_QUERY } from '../../graphql/userManagement'
import {
  addDaysToDate,
  DateObject,
  getTodayDate,
  gregorianToJalaali
} from '../../helpers/date'
import { getRoleFromStorage } from '../../helpers/userData'
import { getTranslate, lang } from '../../localization'
import { ROLE } from '../../staticRoleManagement/types'
import {
  InputData,
  IntegrityStatus,
  LogsData,
  LogStatus,
  SortValue
} from '../../types/auditLogs'
import { EntityList } from '../../types/entityList'
import { EntityType } from '../../types/reserveMonitoringList'
import { RoleBrief } from '../../types/role'
import { UserList } from '../../types/users'
import useStyles from './styles/AuditLogsFilters.styles'

type AuditLogsFiltersProps = {
  actionNameValue: any
  setActionNameValue: React.Dispatch<any>
  dateObject: DateObject
  setDateObject: React.Dispatch<React.SetStateAction<DateObject>>
  keywordsFieldValues: any
  setKeywordsFieldValues: React.Dispatch<any>
  getAuditLogsData: LazyQueryExecFunction<
    {
      auditLogs: LogsData
    },
    OperationVariables
  >
  loadingForAuditLogs: boolean
  perPage: number
  setPage: React.Dispatch<React.SetStateAction<number>>
  setInputData: React.Dispatch<React.SetStateAction<InputData>>
  inputData: InputData
  actionNames: {
    label: string
    value: string
  }[]
  loadingForActionNames: boolean
  isRoleAdmin: boolean
}

export default function AuditLogsFilters({
  actionNameValue,
  setActionNameValue,
  dateObject,
  setDateObject,
  keywordsFieldValues,
  setKeywordsFieldValues,
  getAuditLogsData,
  loadingForAuditLogs,
  perPage,
  setPage,
  setInputData,
  inputData,
  actionNames,
  loadingForActionNames,
  isRoleAdmin
}: AuditLogsFiltersProps) {
  const classes = useStyles()
  const datePickerRef = useRef<HTMLDivElement | null>(null)
  const [timePickerIsOpen, settimePickerIsOpen] = useState<boolean>(false)
  const [autoCompleteLabelForUser, setAutoCompleteLabelForUser] = useState<
    any[]
  >([])
  const [options, setOptions] = useState<EntityList[]>([])
  const [autoCompleteForEntity, setAutoCompleteForEntity] = useState<any>(null)
  const [entityType, setEntityType] = useState<EntityType>('')
  const [allUserEmails, setAllUserEmails] = useState<string[]>([])

  const EntityTypeList: EntityType[] = [
    'Tenant',
    'Accommodation',
    'Channel',
    'Provider'
  ]

  const isRoleTenant = getRoleFromStorage() === ROLE.TENANT
  const isRoleProvider = getRoleFromStorage() === ROLE.PROVIDER

  const { data: usersData, loading: loadingForUsers } = useQuery<{
    users: UserList
  }>(USERS_LIST_QUERY, {
    notifyOnNetworkStatusChange: true,
    onCompleted: (res) => {
      const temp = res.users.users.map((user) => user.email)
      setAllUserEmails(temp)
    }
  })

  const { data: rolesData } = useQuery<{
    roles: Pick<RoleBrief, 'name'>[]
  }>(AUDIT_LOG_ROLE_NAMES_QUERY, {
    variables: { filters: {} },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only'
  })

  const [getEntityList, { loading: loadingForEntityList }] = useLazyQuery<{
    entityList: EntityList[]
  }>(ENTITY_LIST_QUERY, {
    onCompleted: (res) => {
      setOptions(res.entityList)
    },
    onError: (err) => {
      toast.error(err.message)
    }
  })

  useEffect(() => {
    actionNames.sort((a, b) => {
      const labelA = a.label.toUpperCase()
      const labelB = b.label.toUpperCase()
      if (labelA < labelB) {
        return -1 // labelA comes before labelB in alphabetical order
      }
      if (labelA > labelB) {
        return 1 // labelA comes after labelB in alphabetical order
      }
      return 0 // names are equal
    })

    const collator = new Intl.Collator('fa', { sensitivity: 'base' })
    actionNames.sort((a, b) => collator.compare(a.label, b.label))
  }, [actionNames.length])

  useEffect(() => {
    /**
     * Alert if clicked on outside of element
     */
    function handleClickOutside(event: MouseEvent) {
      if (
        datePickerRef.current &&
        !datePickerRef?.current?.contains(event.target as Node) &&
        timePickerIsOpen
      ) {
        settimePickerIsOpen(false)
      }
    }

    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [datePickerRef, timePickerIsOpen])

  useEffect(() => {
    setActionNameValue({
      value: inputData.action,
      label: actionNames.find((v) => v.value === inputData.action)?.label
    })
    setKeywordsFieldValues(inputData.keywords)
  }, [inputData.action, inputData.keywords, loadingForActionNames])

  useEffect(() => {
    if (autoCompleteLabelForUser.length === 0) {
      setInputData({
        ...inputData,
        userEmails: isRoleTenant || isRoleProvider ? allUserEmails : []
      })
    } else {
      const tempUserEmails = autoCompleteLabelForUser.map((ele) => ele.value)
      setInputData({
        ...inputData,
        userEmails: tempUserEmails
      })
    }
  }, [autoCompleteLabelForUser.length, allUserEmails.length])

  useEffect(() => {
    if (isRoleTenant) {
      setEntityType('Accommodation')
      getEntityList({
        variables: {
          entityType: 'Accommodation'
        }
      })
    } else if (isRoleProvider) {
      setEntityType('Provider')
      getEntityList({
        variables: {
          entityType: 'Provider'
        }
      })
    }
  }, [isRoleTenant, isRoleProvider])

  const statusList: LogStatus[] = ['Success', 'Error']
  const integrityStatusList: IntegrityStatus[] = ['Ok', 'Warning']
  const connectionMethodList = ['password', '2fa']
  const accountTypeList = Array.from(
    new Set(rolesData?.roles?.map((role) => role.name).filter(Boolean) || [])
  ).sort()
  const sortOptions: { label: string; value: SortValue }[] = [
    { label: 'Date — newest first', value: 'DATE_DESC' },
    { label: 'Date — oldest first', value: 'DATE_ASC' },
    { label: 'User — Z to A', value: 'USER_DESC' },
    { label: 'User — A to Z', value: 'USER_ASC' },
    { label: 'Action — Z to A', value: 'ACTION_DESC' },
    { label: 'Action — A to Z', value: 'ACTION_ASC' },
    { label: 'User IP — descending', value: 'USER_IP_DESC' },
    { label: 'User IP — ascending', value: 'USER_IP_ASC' },
    { label: 'Account Type — Z to A', value: 'ACCOUNT_TYPE_DESC' },
    { label: 'Account Type — A to Z', value: 'ACCOUNT_TYPE_ASC' },
    {
      label: 'Connection Method — Z to A',
      value: 'CONNECTION_METHOD_DESC'
    },
    {
      label: 'Connection Method — A to Z',
      value: 'CONNECTION_METHOD_ASC'
    }
  ]

  return (
    <div className={classes.root}>
      <div className={classes.filtersContainer}>
        <div className={classes.actionAndKeywordsContainer}>
          <>
            <div className={classes.actionAutocompleteContainer}>
              <AutocompleteWithLabel
                options={actionNames}
                value={actionNameValue}
                fullWidth
                getOptionLabel={(option) => option.label || ''}
                label={getTranslate('Action')}
                onChange={(_, v: any) => {
                  if (v) {
                    setInputData({
                      ...inputData,
                      action: v.value
                    })
                  } else {
                    setInputData({
                      ...inputData,
                      action: ''
                    })
                  }
                }}
                inputSize="small"
                loading={loadingForActionNames}
                loadingText={getTranslate('Loading') + '...'}
              />
              <AutocompleteWithLabel
                value={autoCompleteLabelForUser}
                showSelectAll
                multiple
                limitTags={1}
                fullWidth
                getOptionLabel={(option) => option.label || ''}
                label={getTranslate('User')}
                inputSize="small"
                size="small"
                options={
                  usersData
                    ? usersData?.users.users.map((userItem) => ({
                        label: `${userItem.name} (${userItem.email})`,
                        value: userItem.email,
                        id: userItem.ID,
                        code: userItem.email
                      }))
                    : []
                }
                getOptionSelected={(option, value) =>
                  option.value === value.value
                }
                onChange={(_, v: any) => {
                  if (v.length > 0) {
                    const tempSelectedUsers = v.map((item: any) => ({
                      label: item.label,
                      value: item.value,
                      code: item.code,
                      id: item.id
                    }))

                    setAutoCompleteLabelForUser(tempSelectedUsers)
                  } else {
                    setAutoCompleteLabelForUser([])
                  }
                }}
                loading={loadingForUsers}
                loadingText={getTranslate('Loading') + '...'}
              />
            </div>
            <div className={classes.actionAutocompleteContainer}>
              <SelectWithLabel
                value={entityType}
                onChange={(event) => {
                  setEntityType(event.target.value as EntityType)
                  getEntityList({
                    variables: {
                      entityType: event.target.value
                    }
                  })
                  setAutoCompleteForEntity(null)
                  setInputData({
                    ...inputData,
                    entityId: '',
                    entityCode: ''
                  })
                }}
                fullWidth
                label={getTranslate('Entity Type')}
                size="small"
                disabled={isRoleTenant || isRoleProvider}>
                {EntityTypeList.map((item, index) => (
                  <MenuItem key={index} value={item}>
                    {getTranslate(item)}
                  </MenuItem>
                ))}
              </SelectWithLabel>
              <AutocompleteWithLabel
                name="EntityId"
                options={loadingForEntityList ? [] : options}
                size="small"
                fullWidth
                getOptionLabel={(option) => option.label || ''}
                label={getTranslate('Entity')}
                value={autoCompleteForEntity}
                inputSize="small"
                onChange={(_, value: any) => {
                  if (value) {
                    setAutoCompleteForEntity({
                      value: value.value,
                      label: value.label
                    })
                    setInputData({
                      ...inputData,
                      entityId: value.value,
                      entityCode: value.entityCode
                    })
                  } else {
                    setAutoCompleteForEntity(null)
                    setInputData({
                      ...inputData,
                      entityId: '',
                      entityCode: ''
                    })
                  }
                }}
                loading={loadingForEntityList}
                loadingText={getTranslate('Loading') + '...'}
              />
            </div>
            <div className={classes.actionAutocompleteContainer}>
              <FreeSoloAutocompleteWithLabel
                label={getTranslate('Keywords')}
                placeholder={getTranslate(
                  'Enter keywords with at least 3 characters to search by among Audit Logs'
                )}
                onChange={(_, v: any) => {
                  if (v.length > 0) {
                    var lastOneElement = v.length - 1
                    return (
                      v[lastOneElement].length > 2 &&
                      setInputData({
                        ...inputData,
                        keywords: v
                      })
                    )
                  } else {
                    setInputData({
                      ...inputData,
                      keywords: []
                    })
                  }
                }}
                value={keywordsFieldValues}
                options={[]}
                message={''}
                regexPattern={/.*/}
                inputSize="small"
              />
              <InputWithLabel
                label={getTranslate('User IP')}
                placeholder={getTranslate('Search with User IP')}
                autoComplete="off"
                noHelperText
                inputSize="medium"
                value={inputData.userIP}
                onChange={(event) => {
                  setInputData({
                    ...inputData,
                    userIP: event.currentTarget.value.trim()
                  })
                }}
              />
              {isRoleAdmin && (
                <InputWithLabel
                  label={getTranslate('Correlation ID')}
                  placeholder={getTranslate('Search with Correlation ID')}
                  autoComplete="off"
                  noHelperText
                  inputSize="medium"
                  value={inputData.correlationId}
                  onChange={(event) => {
                    setInputData({
                      ...inputData,
                      correlationId: event.currentTarget.value
                    })
                  }}
                />
              )}
            </div>
            <div className={classes.actionAutocompleteContainer}>
              <SelectWithLabel
                value={inputData.accountType}
                onChange={(event) => {
                  setInputData({
                    ...inputData,
                    accountType: event.target.value as string
                  })
                }}
                size="small"
                label={getTranslate('Account Type')}
                fullWidth>
                <MenuItem value="">{getTranslate('All')}</MenuItem>
                {accountTypeList.map((item) => (
                  <MenuItem key={item} value={item}>
                    {getTranslate(item)}
                  </MenuItem>
                ))}
              </SelectWithLabel>
              <SelectWithLabel
                value={inputData.connectionMethod}
                onChange={(event) => {
                  setInputData({
                    ...inputData,
                    connectionMethod: event.target.value as string
                  })
                }}
                size="small"
                label={getTranslate('Connection Method')}
                fullWidth>
                <MenuItem value="">{getTranslate('All')}</MenuItem>
                {connectionMethodList.map((item) => (
                  <MenuItem key={item} value={item}>
                    {getTranslate(item === '2fa' ? 'Two-factor' : 'Password')}
                  </MenuItem>
                ))}
              </SelectWithLabel>
            </div>
            <div className={classes.actionAutocompleteContainer}>
              <SelectWithLabel
                value={inputData.status ? inputData.status : null}
                onChange={(event) => {
                  setInputData({
                    ...inputData,
                    status: event.target.value as LogStatus
                  })
                }}
                size="small"
                label={getTranslate('Status')}
                fullWidth>
                {statusList.map((item, index) => (
                  <MenuItem
                    key={index}
                    value={item}
                    classes={{
                      root: lang === 'fa' ? classes.menuItem : undefined
                    }}>
                    {lang === 'fa' ? getTranslate(item as string) : item}
                  </MenuItem>
                ))}
              </SelectWithLabel>
              <SelectWithLabel
                value={
                  inputData.integrityStatus ? inputData.integrityStatus : ''
                }
                onChange={(event) => {
                  setInputData({
                    ...inputData,
                    integrityStatus: event.target.value as IntegrityStatus
                  })
                }}
                size="small"
                label={getTranslate('Integrity Status')}
                fullWidth>
                {integrityStatusList.map((item, index) => (
                  <MenuItem
                    key={index}
                    value={item}
                    classes={{
                      root: lang === 'fa' ? classes.menuItem : undefined
                    }}>
                    {lang === 'fa' ? getTranslate(item as string) : item}
                  </MenuItem>
                ))}
              </SelectWithLabel>
              <SelectWithLabel
                value={inputData.sort}
                onChange={(event) => {
                  setInputData({
                    ...inputData,
                    sort: event.target.value as SortValue
                  })
                }}
                size="small"
                label={getTranslate('Sort By')}
                fullWidth>
                {sortOptions.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {getTranslate(item.label)}
                  </MenuItem>
                ))}
              </SelectWithLabel>
            </div>
          </>
        </div>
        <div ref={datePickerRef} className={classes.datePickerContainer}>
          <TimePeriodForm
            isOpen={timePickerIsOpen}
            setTimePickerSelected={settimePickerIsOpen}
            dateObject={dateObject}
            onChange={(data: DateObject) => {
              setDateObject(data)
            }}
            onDoneClickForCloseDatePicker={timePickerIsOpen}
            setOnDoneClickForCloseDatePicker={settimePickerIsOpen}
            enableAllDays
            disableAfter={addDaysToDate(getTodayDate(), 1)}
            endDateCanBeSelectAsStartDate={false}
          />
        </div>
      </div>
      <div className={classes.buttonsContainer}>
        <Button
          variant="text"
          color="secondary"
          onClick={() => {
            const today = getTodayDate()
            const tomorrow = addDaysToDate(today, 1)
            const resetInput: InputData = {
              perPage: perPage,
              from: today,
              keywords: [],
              action: '',
              userEmails: [],
              page: 1,
              requestID: '',
              to: tomorrow,
              entityCode: '',
              entityId: '',
              correlationId: '',
              userIP: '',
              accountType: '',
              connectionMethod: '',
              sort: 'DATE_DESC',
              status: undefined,
              integrityStatus: undefined
            }
            const willChangePage = inputData.page !== 1
            setPage(1)
            setInputData(resetInput)
            setAutoCompleteLabelForUser([])
            setEntityType('')
            setAutoCompleteForEntity(null)
            setDateObject({
              gregorianStartDate: today,
              gregorianEndDate: tomorrow,
              jalaaliStartDate: gregorianToJalaali(today),
              jalaaliEndDate: gregorianToJalaali(tomorrow)
            })
            if (!willChangePage) {
              getAuditLogsData({ variables: { input: resetInput } })
            }
          }}>
          {getTranslate('Reset')}
        </Button>
        <Button
          disabled={loadingForAuditLogs || !inputData.to}
          variant="contained"
          color="primary"
          onClick={() => {
            const willChangePage = inputData.page !== 1
            setPage(1)
            const searchInput = { ...inputData, page: 1, requestID: '' }
            setInputData(searchInput)
            if (!willChangePage) {
              getAuditLogsData({ variables: { input: searchInput } })
            }
          }}>
          {getTranslate('Search')}
          {loadingForAuditLogs && (
            <CircularProgress
              className={classes.circularProgress}
              size={16}
              color="primary"
            />
          )}
        </Button>
      </div>
    </div>
  )
}
